import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LineChart as ChartIcon, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { apiErrorMessage } from '@/api/client'
import { payroll as payrollApi } from '@/api/endpoints'
import { Alert, HelpTip, Input, Modal, Spinner } from '@/components/ui'
import { currencySymbol, formatDate, formatMoney, toISODate } from '@/lib/utils'
import type { InstructorRateRow } from '@/types/api'

import { rewardTypeLabel } from './typeLabel'

/**
 * Sadzby inštruktora (`sql/039`) - v karte inštruktora (záložka Odmeny)
 * aj vo výplatnom liste. Hodinová sadzba hore priamo v poli, ostatné druhy
 * odmien pod „Ďalšie sadzby“. Sadzba platí od dátumu; bez nej platí sadzba
 * školy. Uložením sa nevyplatené odmeny od toho dňa prepočítajú. „História
 * a graf“ ukáže, kedy a na akú sumu sa sadzba menila.
 */
export default function InstructorRates({ instructorId, readOnly }: { instructorId: number; readOnly?: boolean }) {
  const { t } = useTranslation()
  const [showOther, setShowOther] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['instructor-rates', instructorId],
    queryFn: () => payrollApi.rates(instructorId),
  })

  if (isLoading || !data) {
    return (
      <div className="flex h-16 items-center justify-center text-slate-400">
        <Spinner className="h-4 w-4" />
      </div>
    )
  }
  const hourly = data.find((row) => row.type_name === 'hourly-rate')
  const other = data.filter((row) => row !== hourly)

  return (
    <div className="space-y-2 rounded-md border border-slate-200 p-3">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        {t('payroll.ratesTitle')}
        <HelpTip label={t('payroll.ratesTitle')}>{t('payroll.ratesHint')}</HelpTip>
      </h3>
      {hourly ? <RateRow instructorId={instructorId} row={hourly} readOnly={readOnly} prominent /> : null}
      {other.length ? (
        <>
          <button type="button" className="text-xs text-action-700 hover:underline" onClick={() => setShowOther(!showOther)}>
            {showOther ? t('payroll.hideOtherRates') : t('payroll.showOtherRates', { count: other.length })}
          </button>
          {showOther
            ? other.map((row) => <RateRow key={row.type_id} instructorId={instructorId} row={row} readOnly={readOnly} />)
            : null}
        </>
      ) : null}
    </div>
  )
}

function useRefreshRates() {
  const qc = useQueryClient()
  return () => {
    for (const key of ['instructor-rates', 'instructor-rewards', 'rewards-overview', 'rewards']) {
      void qc.invalidateQueries({ queryKey: [key] })
    }
  }
}

function RateRow({
  instructorId,
  row,
  readOnly,
  prominent,
}: {
  instructorId: number
  row: InstructorRateRow
  readOnly?: boolean
  prominent?: boolean
}) {
  const { t } = useTranslation()
  const refresh = useRefreshRates()
  const current = row.effective_rate ?? row.rate ?? row.school_rate
  const [rate, setRate] = useState(current)
  const [from, setFrom] = useState(() => toISODate(new Date()))
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [chart, setChart] = useState(false)

  // Po uložení (alebo zmazaní sadzby v histórii) sa pole zarovná s novou sadzbou.
  useEffect(() => setRate(current), [current])

  const save = useMutation({
    mutationFn: () =>
      payrollApi.setRate(instructorId, {
        reward_type_id: row.type_id,
        rate: String(Number.parseFloat(String(rate).replace(',', '.'))),
        valid_from: from,
      }),
    onSuccess: () => {
      setError(null)
      setSaved(true)
      // Budúca sadzba aktuálnu nemení - pole sa vráti na platnú sadzbu,
      // naplánovaná zmena sa ukáže pod ním.
      setRate(current)
      setFrom(toISODate(new Date()))
      refresh()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const parsed = Number.parseFloat(String(rate).replace(',', '.'))
  const valid = String(rate).trim() !== '' && !Number.isNaN(parsed) && parsed >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(from)
  const changed = valid && parsed !== Number.parseFloat(current)
  const unit = row.type_name === 'hourly-rate' || row.per === 'hour' ? ` / ${t('payroll.hourShort')}` : ''

  return (
    <div className={prominent ? '' : 'border-t border-slate-100 pt-2'}>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={prominent ? 'text-sm font-medium' : 'text-[13px] text-slate-700'}>
          {rewardTypeLabel(t, row)}
        </span>
        <span className="text-xs text-slate-500">
          {row.effective_source === 'own'
            ? t('payroll.ownRateSince', { date: formatDate(row.valid_from) })
            : row.effective_source === 'rewards'
              ? t('payroll.fromRewards')
              : t('payroll.schoolRate')}
        </span>
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1 text-xs text-action-700 hover:underline"
          onClick={() => setChart(true)}
        >
          <ChartIcon size={13} />
          {t('payroll.historyChart')}
        </button>
      </div>

      {readOnly ? (
        <div className={prominent ? 'text-lg font-semibold tabular-nums' : 'font-medium tabular-nums'}>
          {formatMoney(current)}
          {unit}
        </div>
      ) : (
        <form
          className="mt-1 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (changed) save.mutate()
          }}
        >
          <label className="block">
            <span className="label">{prominent ? t('payroll.currentHourly') : t('payroll.rate')}</span>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={0}
                step="0.01"
                className="w-24 text-right"
                value={rate}
                onChange={(e) => {
                  setSaved(false)
                  setRate(e.target.value)
                }}
              />
              <span className="text-sm text-slate-600">
                {currencySymbol()}
                {unit}
              </span>
            </div>
          </label>
          {changed ? (
            <>
              <label className="block">
                <span className="label">{t('payroll.validFrom')}</span>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <button type="submit" className="btn-primary" disabled={save.isPending}>
                {save.isPending ? <Spinner /> : null}
                {t('global.save')}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setRate(current)}>
                {t('global.cancel')}
              </button>
              <p className="w-full text-xs text-slate-500">{t('payroll.repriceHint')}</p>
            </>
          ) : saved ? (
            <span className="pb-2 text-xs text-green-700">{t('payroll.rateSaved')}</span>
          ) : null}
        </form>
      )}
      {row.upcoming.length ? (
        <p className="mt-1 text-xs text-amber-700">
          {t('payroll.planned')}{' '}
          {row.upcoming
            .slice()
            .sort((a, b) => a.valid_from.localeCompare(b.valid_from))
            .map((u) => t('payroll.plannedItem', { rate: formatMoney(u.rate), date: formatDate(u.valid_from) }))
            .join(', ')}
        </p>
      ) : null}

      {chart ? (
        <RateHistoryModal row={row} readOnly={readOnly} onClose={() => setChart(false)} />
      ) : null}
    </div>
  )
}

/**
 * Kedy a na akú sumu sa sadzba menila - schodový graf (každá sadzba platí do
 * ďalšej) a pod ním zoznam zmien. Prerušovaná čiara je sadzba školy, ktorá
 * platí tam, kde inštruktor vlastnú nemá.
 */
function RateHistoryModal({
  row,
  readOnly,
  onClose,
}: {
  row: InstructorRateRow
  readOnly?: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const refresh = useRefreshRates()
  const [error, setError] = useState<string | null>(null)
  const remove = useMutation({
    mutationFn: (id: number) => payrollApi.deleteRate(id),
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const school = Number.parseFloat(row.school_rate)
  // Od najstaršej; posledný bod je dnes (alebo posledná budúca zmena), aby
  // bolo vidno, dokedy sadzba platí.
  const changes = useMemo(
    () => [...row.history].sort((a, b) => a.valid_from.localeCompare(b.valid_from) || a.id - b.id),
    [row.history],
  )
  const points = useMemo(() => {
    const today = toISODate(new Date())
    const out = changes.map((h) => ({ date: h.valid_from, rate: Number.parseFloat(h.rate) }))
    // Prvá vlastná sadzba až v budúcnosti - dovtedy platí sadzba školy.
    if (out.length && out[0].date > today) out.unshift({ date: today, rate: school })
    const lastDate = out.length ? out[out.length - 1].date : today
    if (out.length && lastDate < today) out.push({ date: today, rate: out[out.length - 1].rate })
    return out
  }, [changes, school])

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`${t('payroll.historyTitle')} — ${rewardTypeLabel(t, row)}`}
      footer={
        <button type="button" className="btn-primary" onClick={onClose}>
          {t('global.close')}
        </button>
      }
    >
      <div className="space-y-3">
        {error ? <Alert variant="error">{error}</Alert> : null}
        {changes.length === 0 ? (
          <Alert variant="info">{t('payroll.noOwnRate', { rate: formatMoney(row.school_rate) })}</Alert>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tickFormatter={(v: string) => formatDate(v)} tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  width={60}
                  domain={[0, (max: number) => Math.ceil(Math.max(max, school) * 1.2)]}
                  tickFormatter={(v: number) => formatMoney(v)}
                />
                <Tooltip
                  labelFormatter={(v) => t('payroll.validFromDate', { date: formatDate(String(v)) })}
                  formatter={(value) => [formatMoney(value as number), t('payroll.rate')]}
                />
                <ReferenceLine
                  y={school}
                  stroke="#94a3b8"
                  strokeDasharray="6 4"
                  label={{ value: t('payroll.schoolRateLine'), position: 'insideTopLeft', fontSize: 11, fill: '#64748b' }}
                />
                <Line type="stepAfter" dataKey="rate" stroke="#b91c1c" strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {changes.length ? (
          <table className="table-base [&_tbody_td]:py-1">
            <thead>
              <tr>
                <th>{t('payroll.validFrom')}</th>
                <th className="text-right">{t('payroll.rate')}</th>
                <th className="text-right">{t('payroll.change')}</th>
                <th>{t('payroll.enteredAt')}</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {changes.map((h, index) => {
                const previous = index ? Number.parseFloat(changes[index - 1].rate) : school
                const delta = Number.parseFloat(h.rate) - previous
                return (
                  <tr key={h.id}>
                    <td className="whitespace-nowrap">
                      {formatDate(h.valid_from)}
                      {h.derived ? (
                        <span
                          className="ml-1.5 rounded bg-slate-100 px-1 text-[11px] text-slate-500"
                          title={t('payroll.derivedHint')}
                        >
                          {t('payroll.derived')}
                        </span>
                      ) : null}
                      {row.upcoming.some((u) => u.id === h.id) ? (
                        <span className="ml-1.5 rounded bg-amber-50 px-1 text-[11px] text-amber-700">{t('payroll.upcoming')}</span>
                      ) : null}
                    </td>
                    <td className="text-right font-medium tabular-nums">{formatMoney(h.rate)}</td>
                    <td className={`text-right tabular-nums ${delta > 0 ? 'text-green-700' : delta < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                      {delta > 0 ? '+' : delta < 0 ? '−' : ''}
                      {formatMoney(Math.abs(delta))}
                    </td>
                    <td className="text-slate-500">{h.derived ? '—' : formatDate(h.created)}</td>
                    <td>
                      {!readOnly && !h.derived ? (
                        <button
                          type="button"
                          className="text-slate-400 hover:text-red-600"
                          title={t('global.delete')}
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(t('payroll.deleteRateConfirm'))) remove.mutate(h.id)
                          }}
                        >
                          <X size={14} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : null}
      </div>
    </Modal>
  )
}
